import React, { Fragment } from 'react';

import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import clsx from 'clsx';

import { useFeatures } from '@atc/features';

import {
    InfoIcon,
    List,
} from 'reaxl';
import { EmailCTA } from 'reaxl-email';
import {
    OwnerPhoneNumber,
    OwnerWebsiteCTA,
} from 'reaxl-molecules';
import { EsntialBuyNowCTA } from 'reaxl-wallet';

import { fallbackInterestRates } from 'axl-config';

import dealsDuck from '@/ducks/dealsDuck.js';
import interestRateDuck from '@/ducks/interestRateDuck.js';
import paymentsDuck from '@/ducks/paymentsDuck.js';

import saveDealXgFetcher from '@/fetchers/ExportToDealXgFetcher';

/**
 *  Describe the component
 *
 */
function InventoryAlphaFooter({
    orientation,
    listing,
    emailOwnerProps = {},
    isPhoneNumberCallable,
}) {
    const {
        call: [isCallEnabled],
        email: [isEmailEnable, { override_hide_email: enableConfirmAvailabilityCta }],
        srp_esntial_cta: [enableSRPEsntialCTA, { cta_label: esntialCtaLabel }],
        show_alpha_criteria: [showAlphaCriteria],
    } = useFeatures([
        'call',
        'email',
        'srp_esntial_cta',
        'show_alpha_criteria',
    ]);
    const environment = process.env.NODE_ENV === 'production' ? 'prod' : 'staging';
    const selectedRates = useSelector(interestRateDuck.selectors.getDuckState);
    const nationalRates = selectedRates || fallbackInterestRates;

    const {
        owner = {},
    } = listing;

    const {
        hideEmailForm,
        name,
        phone,
        shipToStore,
        website,
    } = owner;

    const displayOwnerName = listing.ownerName || name;

    const isPortrait = orientation === 'portrait';

    const hasOwnerWebsite = !!website?.href;

    // show email CTA only when dealer has a phone number
    // remove isVisible below once monolith is retired
    const phoneVisible = phone && (phone.visible || phone.isVisible);
    const isEmailCtaEnabled = isEmailEnable && enableConfirmAvailabilityCta;
    const isEmailEligible = emailOwnerProps && phoneVisible && !hideEmailForm;

    const showEmailOwnerCTA = isEmailCtaEnabled || isEmailEligible;

    const showContactInfo = (phone || showEmailOwnerCTA);

    const classNames = clsx('static-height-footer', {
        'display-flex flex-column': isPortrait,
    });

    const analyticsData = {
        inventoryId: listing.id,
        ownerId: owner.id,
        clickType: 'alpha',
    };

    const ownerInfoList = [];

    const sellerPhone = (
        <div className="display-flex">
            <OwnerPhoneNumber
                className="display-block"
                key="listingFooterOwnerPhone"
                phone={phone}
                isCallable={isPhoneNumberCallable}
                analyticsData={analyticsData}
            />
        </div>
    );

    if (showContactInfo && isCallEnabled && !isPhoneNumberCallable && phone) {
        ownerInfoList.push(
            <div
                className="text-subdued display-flex"
                data-cmp="contactInfo"
                key="listingFooterContactInfo"
            >
                {sellerPhone}
            </div>
        );
    }

    const offsiteOwnerTitle = (
        <OwnerWebsiteCTA
            analyticsData={analyticsData}
            className="text-gray-base"
            href={website?.href}
            key="offsiteOwnerTitle"
            label={displayOwnerName}
            uiContext="link"
        />
    );

    const ownerTitle = (
        <span
            className="text-gray-base"
            key="ownerTitle"
        >
            {displayOwnerName}
        </span>
    );

    const renderTitle = (
        <div>
            {showAlphaCriteria && (
                <>
                    Sponsored by
                    {' '}
                </>
            )}
            {hasOwnerWebsite ? offsiteOwnerTitle : ownerTitle}
        </div>
    );

    const hasEcommWebsite = listing?.ecommWebsite?.href;

    return (
        <div className="listing-footer text-subdued-lightest text-size-100">
            <hr />
            {isPortrait && (
                <div
                    className={classNames}
                    data-cmp="cntnr-listing-footer"
                >
                    <div className="text-left margin-bottom-2">
                        {shipToStore && <span className="margin-right-1">Can be shipped to</span>}
                        <div className="text-subdued">
                            {renderTitle}
                            {isCallEnabled && phone && isPhoneNumberCallable && sellerPhone}
                            {shipToStore
                                    && (
                                        <InfoIcon
                                            placement="top"
                                            infoText="This dealership might not have this vehicle on their lot right now,
                                        but they can have it shipped to them. Talk to the dealer to arrange it."
                                            className="margin-left-1"
                                        />
                                    )}
                        </div>
                        <div className="display-flex">
                            <List
                                items={ownerInfoList}
                                inline
                                nolistdecoration={ownerInfoList.length < 2}
                            />
                        </div>
                    </div>
                </div>
            )}

            {isPortrait && (
                <div className="display-flex gap-2 margin-top-2">
                    {(!enableSRPEsntialCTA || (enableSRPEsntialCTA && !hasEcommWebsite)) && showEmailOwnerCTA && (
                        <Fragment key="emailOwnerCTA">
                            <EmailCTA
                                clickType="alpha"
                                key="email-cta"
                                listingId={listing.id}
                                context={emailOwnerProps.context}
                                onClick={emailOwnerProps.onCTAClick}
                                buttonStyle="default"
                                buttonSize="small"
                                className="flex-1 display-flex"
                                showIcon={false}
                                type="block"
                                labelKey={emailOwnerProps.labelKey}
                            />
                        </Fragment>
                    )}
                    {isCallEnabled && phone && isPhoneNumberCallable && (
                        <div className="display-flex">
                            <OwnerPhoneNumber
                                className="display-block"
                                key="listingFooterOwnerPhone"
                                phone={phone}
                                isCallable={isPhoneNumberCallable}
                                analyticsData={analyticsData}
                                uiContext="button"
                                buttonStyle="default"
                                buttonSize="small"
                                buttonLabel="Call"
                            />
                        </div>
                    )}
                    {enableSRPEsntialCTA && hasEcommWebsite && (
                        <EsntialBuyNowCTA
                            dealsDuck={dealsDuck}
                            label={esntialCtaLabel}
                            href={listing?.ecommWebsite?.href}
                            analyticsData={{
                                placement: 'srp-hyundai-experience',
                                listingId: listing?.id,
                            }}
                            bsSize="small"
                            bsStyle="default"
                            paymentsDuck={paymentsDuck}
                            nationalRates={nationalRates}
                            listing={listing}
                            ownerTrackingId={listing?.ownerId}
                            saveDealXgFetcher={saveDealXgFetcher}
                            env={environment}
                            block
                        />
                    )}
                </div>
            )}
        </div>
    );
}

InventoryAlphaFooter.propTypes = {
    /**
     * The emailOwnerProps Object has the props to pass through to the ConnectedEmailOwner component.
     */
    emailOwnerProps: PropTypes.object,
    /**
     * The listing object containing all listing data
     */
    listing: PropTypes.shape({
        ownerId: PropTypes.string,
        ecommWebsite: PropTypes.shape({
            href: PropTypes.string.isRequired,
        }),
    }),
};

export default React.memo(InventoryAlphaFooter);
